import React, { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { Copy, Loader2, Pencil, Plus, Save, Trash2, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { formatDate } from "../../utils/date/common"

const SINGLE_PRICE_TYPES = ["rent", "private"]
const ZEROED_FOR_SINGLE = [
  "child_sum",
  "child_sum_netto",
  "infant_sum",
  "infant_sum_netto",
  "child_sum_transfer",
  "child_sum_transfer_netto",
  "infant_sum_transfer",
  "infant_sum_transfer_netto",
]

const fieldClass =
  "h-10 w-full rounded-input border border-app-content-faint/30 bg-surface-main px-3 text-app-content " +
  "outline-none transition-colors focus:border-brand-main focus:ring-2 focus:ring-brand-main/20 " +
  "disabled:opacity-disabled disabled:cursor-not-allowed"

const onlyNumber = (v) =>
  String(v)
    .replace(",", ".")
    .replace(/[^0-9.]/g, "")

const toDraft = (p) => ({
  adult: String(p.adult ?? ""),
  transfer_type: p.transfer_type ?? "",
  pax_from: String(p.passenger_from ?? ""),
  pax_to: String(p.passenger_to ?? ""),
})

const Caption = ({ children }) => (
  <span className="mb-1 block text-[11px] text-text-muted">{children}</span>
)

// Одна цена: правим сумму, тип трансфера и диапазон пассажиров прямо в строке
const PriceRow = ({
  price,
  transferTypeOptions,
  onSave,
  onDelete,
  busy,
  editUrl,
  copyUrl,
  showCopy,
}) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState(() => toDraft(price))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  // после сохранения таблица перезагружается — подтягиваем свежие значения
  useEffect(() => {
    setDraft(toDraft(price))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [price.id, price.adult, price.transfer_type, price.passenger_from, price.passenger_to])

  const base = toDraft(price)
  const dirty = Object.keys(base).some((k) => base[k] !== draft[k])

  const set = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
    setError("")
  }

  const save = async () => {
    if (draft.adult === "" || Number.isNaN(Number(draft.adult))) {
      return setError(t("admin.price_required", { defaultValue: "Укажите цену" }))
    }
    if (Number(draft.pax_from) > Number(draft.pax_to)) {
      return setError(
        t("admin.pax_range_error", { defaultValue: "Пассажиров «от» больше, чем «до»" })
      )
    }

    const attrs = {
      adult_sum: draft.adult,
      transfer_type: draft.transfer_type,
      passenger_from: draft.pax_from,
      passenger_to: draft.pax_to,
    }
    if (SINGLE_PRICE_TYPES.includes(draft.transfer_type)) {
      ZEROED_FOR_SINGLE.forEach((k) => (attrs[k] = 0))
    }

    setSaving(true)
    try {
      await onSave(price.id, attrs)
    } catch (e) {
      setError(
        (e?.data?.errors ?? [t("admin.save_error", { defaultValue: "Ошибка сохранения" })]).join(
          ", "
        )
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <li className="px-4 py-3">
      <div className="grid grid-cols-2 sm:grid-cols-[1.2fr_1.5fr_0.8fr_0.8fr] gap-3 items-end">
        <label className="block">
          <Caption>
            {t("admin.price", { defaultValue: "Цена" })}, {price.currency}
          </Caption>
          <input
            type="text"
            inputMode="decimal"
            value={draft.adult}
            onChange={(e) => set("adult", onlyNumber(e.target.value))}
            className={`${fieldClass} text-right`}
          />
        </label>

        <label className="block">
          <Caption>{t("admin.transfer_type", { defaultValue: "Тип трансфера" })}</Caption>
          <select
            value={draft.transfer_type}
            onChange={(e) => set("transfer_type", e.target.value)}
            className={fieldClass}
          >
            {transferTypeOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <Caption>{t("admin.pax_from_short", { defaultValue: "Пасс. от" })}</Caption>
          <input
            type="text"
            inputMode="numeric"
            value={draft.pax_from}
            onChange={(e) => set("pax_from", onlyNumber(e.target.value))}
            className={`${fieldClass} text-right`}
          />
        </label>

        <label className="block">
          <Caption>{t("admin.pax_to_short", { defaultValue: "Пасс. до" })}</Caption>
          <input
            type="text"
            inputMode="numeric"
            value={draft.pax_to}
            onChange={(e) => set("pax_to", onlyNumber(e.target.value))}
            className={`${fieldClass} text-right`}
          />
        </label>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-app-content-faint">
          {(price.from_date || price.to_date) &&
            `${formatDate(price.from_date)} – ${formatDate(price.to_date)}`}
        </p>

        <div className="flex items-center gap-1">
          {error && (
            <motion.span
              initial={{ opacity: 0, x: 6 }}
              animate={{ opacity: 1, x: 0 }}
              className="mr-2 text-xs text-red-500"
            >
              {error}
            </motion.span>
          )}

          <a
            href={editUrl}
            title={t("admin.open_full_form", { defaultValue: "Открыть полную форму" })}
            className="p-1.5 text-text-secondary hover:text-brand-main"
          >
            <Pencil className="w-4 h-4" />
          </a>

          {/* Скрыто до отдельного решения: showCopy = true в PriceCell */}
          {showCopy && (
            <a
              href={copyUrl}
              title={t("admin.copy", { defaultValue: "Копировать" })}
              className="p-1.5 text-text-secondary hover:text-brand-main"
            >
              <Copy className="w-4 h-4" />
            </a>
          )}

          <button
            type="button"
            disabled={busy}
            onClick={() => onDelete([price.id])}
            title={t("admin.delete", { defaultValue: "Удалить" })}
            className="p-1.5 text-red-500 hover:text-red-600 disabled:opacity-disabled"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>

          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            className="ml-1 inline-flex h-9 items-center gap-1.5 rounded-button bg-app-base-strong px-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-app-base disabled:cursor-not-allowed disabled:opacity-disabled"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t("admin.save", { defaultValue: "Сохранить" })}
          </button>
        </div>
      </div>
    </li>
  )
}

// Модалка БЕЗ портала: фиксированный оверлей рендерится прямо в дереве ячейки.
const PriceEditModal = ({
  title,
  subtitle,
  prices,
  transferTypeOptions = [],
  buildEditUrl,
  buildNewUrl,
  baseId,
  typeParam,
  onSave,
  onDelete,
  busyIds,
  onClose,
  showCopy = false,
  showAdd = false,
}) => {
  const { t } = useTranslation()

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose()
    const body = document.body
    const prevOverflow = body.style.overflow
    const prevPaddingRight = body.style.paddingRight

    // Без скроллбара страница стала бы шире и «прыгнула» — добавляем отступ на его ширину
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    if (scrollbarWidth > 0) {
      const current = parseFloat(window.getComputedStyle(body).paddingRight) || 0
      body.style.paddingRight = `${current + scrollbarWidth}px`
    }
    body.style.overflow = "hidden"
    document.addEventListener("keydown", onKey)

    // размонтирование происходит после анимации закрытия — скроллбар возвращается без скачка
    return () => {
      document.removeEventListener("keydown", onKey)
      body.style.overflow = prevOverflow
      body.style.paddingRight = prevPaddingRight
    }
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 text-left"
      role="dialog"
      aria-modal="true"
    >
      <motion.div
        className="absolute inset-0 bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        onClick={onClose}
      />

      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.98 }}
        transition={{ duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="relative flex max-h-[88vh] w-full max-w-3xl flex-col overflow-hidden rounded-card bg-white shadow-lg"
      >
        <div className="flex items-start gap-3 border-b border-app-content-faint/10 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-app-base-strong leading-snug break-words">{title}</p>
            {subtitle && <p className="mt-0.5 text-xs text-text-muted">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            title={t("admin.close", { defaultValue: "Закрыть" })}
            className="p-1.5 text-text-secondary hover:text-app-content"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <ul className="divide-y divide-app-content-faint/10 overflow-y-auto">
          {prices.map((p) => (
            <PriceRow
              key={p.id}
              price={p}
              transferTypeOptions={transferTypeOptions}
              onSave={onSave}
              onDelete={onDelete}
              busy={busyIds.includes(p.id)}
              editUrl={buildEditUrl(p.id)}
              showCopy={showCopy}
              copyUrl={buildNewUrl({ from_id: p.id, copy: 1, transport_type_id: typeParam })}
            />
          ))}
        </ul>

        {/* Скрыто до отдельного решения: showAdd = true в PriceCell */}
        {showAdd && (
          <a
            href={buildNewUrl({ from_id: baseId, transport_type_id: typeParam })}
            className="flex items-center justify-center gap-1.5 border-t border-app-content-faint/10 bg-bg-secondary/50 px-3 py-2.5 text-sm font-medium text-brand-main transition-colors hover:bg-brand-main/5"
          >
            <Plus className="w-4 h-4" />
            {t("admin.add_price", { defaultValue: "Добавить цену" })}
          </a>
        )}
      </motion.div>
    </div>
  )
}

export default PriceEditModal
